import React, { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, MapPin, X } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { FeaturedCard, ResourceRow } from './HcResourceBits';
import { HcSsrMyRequests } from './HcSsrMyRequests';

const ALL = 'all';
const SSR_RESOURCE = {
  id: 'supportive-service-request', title: 'Supportive Service Request', builtin: true, url: '/helpconnect/resources/supportive-service-request',
  description: 'Ask for help with transportation, uniforms, background checks, testing, internet service or tuition.',
  category: 'supportive_services', states: [], featured: false,
};

export default function HcResourcesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState(ALL);
  const [state, setState] = useState(ALL);
  const [ssr, setSsr] = useState({});

  useEffect(() => {
    Promise.all([hcApi.get('/resources'), hcApi.get('/supportive-service-requests').catch(() => ({ data: {} }))])
      .then(([{ data: d }, { data: ssr }]) => {
        const resources = ssr.eligible ? [...d.resources, SSR_RESOURCE] : d.resources;
        setData({ ...d, resources });
        setSsr(ssr);
        if (d.my_state) setState(d.my_state);
      }).catch((e) => setError(hcError(e)));
  }, []);

  const catName = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c.name])), [data]);
  const term = q.trim().toLowerCase();
  const matches = (r) => (state === ALL || !r.states.length || r.states.includes(state))
    && (!term || `${r.title} ${r.description} ${catName[r.category] || ''}`.toLowerCase().includes(term));
  const inState = (data?.resources || []).filter(matches);
  const list = inState.filter((r) => cat === ALL || r.category === cat);
  const featured = cat === ALL && !term ? inState.filter((r) => r.featured) : [];
  const pill = (active) => `shrink-0 h-10 px-4 sm:px-5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${active ? 'bg-[#922A8C] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#922A8C] hover:text-[#922A8C]'}`;

  return (
    <HcLayout>
      <div className="max-w-6xl hc-fade-up min-w-0" data-testid="hc-resources-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">Resources Center</h1>
        <p className="text-gray-500 mt-2">Helpful tools and materials to support your success.</p>

        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources"
              className="w-full h-12 pl-12 pr-10 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40" data-testid="hc-resources-search" />
            {q && <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-label="Clear search" data-testid="hc-resources-search-clear"><X className="w-4 h-4" /></button>}
          </div>
          <label className="relative md:w-56">
            <MapPin className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#922A8C] pointer-events-none" />
            <select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filter by state"
              className="w-full h-12 pl-10 pr-4 rounded-xl border border-gray-200 bg-white font-semibold text-sm text-[#363636] focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40" data-testid="hc-resources-state-filter">
              <option value={ALL}>All States</option>
              {(data?.states || []).map((s) => <option key={s} value={s}>{s}{s === data?.my_state ? ' (your state)' : ''}</option>)}
            </select>
          </label>
        </div>

        <div className="mt-5 -mx-4 px-4 sm:mx-0 sm:px-0 flex gap-2 overflow-x-auto lg:flex-wrap lg:overflow-visible pb-2" data-testid="hc-resources-categories">
          <button onClick={() => setCat(ALL)} className={pill(cat === ALL)} data-testid="hc-resources-cat-all">All Resources</button>
          {(data?.categories || []).map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} className={pill(cat === c.id)} data-testid={`hc-resources-cat-${c.id}`}>{c.name}</button>
          ))}
        </div>

        {!data && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C] mt-6" />}
        {error && <p className="text-red-600 mt-6" data-testid="hc-resources-error">{error}</p>}

        {featured.length > 0 && (
          <section className="mt-6" data-testid="hc-resources-featured">
            <h2 className="text-base md:text-lg font-bold text-[#363636] mb-3">Featured Resources</h2>
            <div className="-mx-4 px-4 sm:mx-0 sm:px-0 flex sm:grid sm:grid-cols-2 xl:grid-cols-3 gap-4 overflow-x-auto snap-x pb-2">
              {featured.map((r) => <FeaturedCard key={r.id} r={r} catName={catName[r.category]} />)}
            </div>
          </section>
        )}

        {data && (
          <section className="mt-6">
            <h2 className="text-base md:text-lg font-bold text-[#363636] mb-3" data-testid="hc-resources-count">
              {cat === ALL ? 'All Resources' : catName[cat]} <span className="text-gray-400 font-semibold">({list.length})</span>
            </h2>
            {list.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-gray-500 text-center" data-testid="hc-resources-empty">
                {data.resources.length === 0 ? 'Resources will appear here soon.' : 'No resources match your search or filters.'}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100" data-testid="hc-resources-list">
                {list.map((r) => <ResourceRow key={r.id} r={r} catName={catName[r.category]} />)}
              </div>
            )}
          </section>
        )}
        {data && cat === 'supportive_services' && (ssr.eligible || ssr.requests?.length > 0) && <HcSsrMyRequests requests={ssr.requests || []} />}
      </div>
    </HcLayout>
  );
}
